import React, { useState, useEffect } from 'react';

const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
const CACHE_TTL = 60 * 60 * 1000; // 60 minutes

// Map OpenWeatherMap icon codes to FontAwesome Sharp icons
const ICON_MAP = {
  '01d': 'fa-sharp fa-solid fa-sun',           // clear sky day
  '01n': 'fa-sharp fa-solid fa-moon',           // clear sky night
  '02d': 'fa-sharp fa-solid fa-cloud-sun',      // few clouds day
  '02n': 'fa-sharp fa-solid fa-cloud-moon',     // few clouds night
  '03d': 'fa-sharp fa-solid fa-cloud',           // scattered clouds
  '03n': 'fa-sharp fa-solid fa-cloud',
  '04d': 'fa-sharp fa-solid fa-clouds',          // overcast
  '04n': 'fa-sharp fa-solid fa-clouds',
  '09d': 'fa-sharp fa-solid fa-cloud-showers',   // shower rain
  '09n': 'fa-sharp fa-solid fa-cloud-showers',
  '10d': 'fa-sharp fa-solid fa-cloud-sun-rain',  // rain day
  '10n': 'fa-sharp fa-solid fa-cloud-moon-rain', // rain night
  '11d': 'fa-sharp fa-solid fa-cloud-bolt',      // thunderstorm
  '11n': 'fa-sharp fa-solid fa-cloud-bolt',
  '13d': 'fa-sharp fa-solid fa-snowflake',       // snow
  '13n': 'fa-sharp fa-solid fa-snowflake',
  '50d': 'fa-sharp fa-solid fa-smog',            // mist/fog
  '50n': 'fa-sharp fa-solid fa-smog',
};

function getCached(zip) {
  try {
    const raw = localStorage.getItem(`weather_cache_${zip}`);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.ts > CACHE_TTL) {
      localStorage.removeItem(`weather_cache_${zip}`);
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
}

function setCache(zip, data) {
  try {
    localStorage.setItem(`weather_cache_${zip}`, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* quota exceeded — ignore */ }
}

function WeatherWidget({ zip }) {
  const [weather, setWeather] = useState(() => zip ? getCached(zip) : null);

  useEffect(() => {
    if (!zip || !API_KEY) return;

    const cached = getCached(zip);
    if (cached) {
      setWeather(cached);
      return;
    }

    let cancelled = false;
    fetch(`https://api.openweathermap.org/data/2.5/weather?zip=${zip},us&units=imperial&appid=${API_KEY}`)
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(json => {
        if (cancelled) return;
        const data = {
          temp: Math.round(json.main.temp),
          icon: json.weather[0]?.icon || '01d',
          desc: json.weather[0]?.main || '',
        };
        setCache(zip, data);
        setWeather(data);
      })
      .catch(() => {
        if (!cancelled) setWeather(null);
      });

    return () => { cancelled = true; };
  }, [zip]);

  if (!zip || !API_KEY || !weather) return null;

  const faClass = ICON_MAP[weather.icon] || 'fa-sharp fa-solid fa-cloud';

  return (
    <span className="navbar-text ml-3" style={{ whiteSpace: 'nowrap' }}>
      <i className={`${faClass} mr-1`} />
      {weather.temp}°F
      <span className="d-none d-xl-inline ml-1">{weather.desc}</span>
    </span>
  );
}

export default WeatherWidget;
